import DOMPurify from 'dompurify';

/**
 * Shared rules for the portal's rich text fields: what the Tiptap toolbar
 * exposes, and what survives sanitization before either the editor or the
 * public site renders it as HTML. Kept in one place so the two never drift.
 */

export const RICH_TEXT_ALLOWED_TAGS = ['p', 'br', 'strong', 'em', 'u', 'ul', 'ol', 'li', 'span'];
export const RICH_TEXT_ALLOWED_ATTR = ['style'];
// Only these CSS properties are kept on a `style` attribute — anything else
// (including url()-based properties) is stripped by DOMPurify's hook below.
const ALLOWED_STYLE_PROPS = ['text-align', 'font-family', 'font-size'];

let hookInstalled = false;
function ensureStyleAllowlistHook() {
  if (hookInstalled) return;
  hookInstalled = true;
  DOMPurify.addHook('uponSanitizeAttribute', (_node, data) => {
    if (data.attrName !== 'style') return;
    const kept = data.attrValue
      .split(';')
      .map((decl) => decl.trim())
      .filter((decl) => {
        const prop = decl.split(':')[0]?.trim().toLowerCase();
        return prop && ALLOWED_STYLE_PROPS.includes(prop);
      })
      .join('; ');
    data.attrValue = kept;
  });
}

/** Sanitizes rich text HTML for storage or rendering — strips anything outside the toolbar's vocabulary. */
export function sanitizeRichText(html: string): string {
  ensureStyleAllowlistHook();
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: RICH_TEXT_ALLOWED_TAGS,
    ALLOWED_ATTR: RICH_TEXT_ALLOWED_ATTR,
  }).trim();
}

/** True when the field has no visible content (covers '', '<p></p>', whitespace-only markup, …). */
export function isRichTextEmpty(html: string): boolean {
  return !html.replace(/<[^>]*>/g, '').trim();
}

/** Plain-text extraction, e.g. to hand off to the AI enhance prompt. */
export function richTextToPlainText(html: string): string {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent ?? '';
}

/** Wraps AI-enhanced plain text back into simple paragraph HTML. */
export function plainTextToRichText(text: string): string {
  return text
    .split(/\n{2,}/)
    .map((para) => para.trim())
    .filter(Boolean)
    .map((para) => `<p>${para.replace(/\n/g, '<br>')}</p>`)
    .join('');
}

export const FONT_FAMILY_OPTIONS = [
  { label: 'Default', value: '' },
  { label: 'Body (DM Sans)', value: "'DM Sans', sans-serif" },
  { label: 'Heading (Cormorant Garamond)', value: "'Cormorant Garamond', serif" },
  { label: 'Accent (Spectral)', value: "'Spectral', serif" },
  { label: 'Georgia', value: 'Georgia, serif' },
  { label: 'Arial', value: 'Arial, sans-serif' },
  { label: 'Times New Roman', value: "'Times New Roman', serif" },
  { label: 'Courier New', value: "'Courier New', monospace" },
] as const;

export const FONT_SIZE_OPTIONS = [
  { label: 'Default', value: '' },
  { label: 'Small (12px)', value: '12px' },
  { label: 'Normal (14px)', value: '14px' },
  { label: 'Medium (16px)', value: '16px' },
  { label: 'Large (18px)', value: '18px' },
  { label: 'X-Large (20px)', value: '20px' },
  { label: 'XX-Large (24px)', value: '24px' },
  { label: 'Huge (32px)', value: '32px' },
] as const;
